import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post("/auth/reset-password/verify", { token })
      .then((r) => { setEmail(r.data.email); setState("valid"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setState("done");
      setTimeout(() => nav("/login"), 1200);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Reset failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6 neec-grid-bg">
      <div className="w-full max-w-sm bg-card border border-border rounded-3xl p-8 shadow-2xl" data-testid="reset-panel">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold">N</div>
          <p className="font-head font-extrabold text-lg">NEEC POS</p>
        </div>
        {state === "checking" && (
          <div className="flex flex-col items-center text-muted-foreground gap-3 py-12"><Loader2 className="w-8 h-8 animate-spin" /> Checking link…</div>
        )}
        {state === "invalid" && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4"><ShieldAlert className="w-7 h-7" /></div>
            <h2 className="font-head text-2xl font-extrabold">Link expired</h2>
            <p className="text-muted-foreground mt-2 mb-6">This reset link is invalid or has expired.</p>
            <Button onClick={() => nav("/login")} variant="outline" data-testid="reset-to-login">Back to Login</Button>
          </div>
        )}
        {state === "done" && (
          <div className="text-center py-8" data-testid="reset-success">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" /></div>
            <h2 className="font-head text-2xl font-extrabold">Password updated</h2>
            <p className="text-muted-foreground mt-2">Redirecting you to sign in…</p>
          </div>
        )}
        {state === "valid" && (
          <>
            <h2 className="font-head text-2xl font-extrabold flex items-center gap-2"><Zap className="w-5 h-5 text-emerald-500" /> Choose a new password</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-6">for <span className="mono">{email}</span></p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">New Password</Label>
                <Input value={pw} onChange={(e) => setPw(e.target.value)} type="password" className="h-12 mt-1.5" placeholder="At least 8 characters" data-testid="reset-password" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Confirm Password</Label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" className="h-12 mt-1.5" data-testid="reset-confirm" />
              </div>
              {error && <p className="text-sm text-rose-500" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={saving} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="reset-submit">
                {saving ? "Saving…" : "Reset Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
